import type { TileType } from './constants.js';

/**
 * Normalized metric keys each tile type exposes after `normalize()` (ADR-002 §3.3, §5).
 *
 * The glance grammar is only honest if `glance.primary/delta/state` name metrics
 * that actually exist in the tile's normalized data (ADR-002 §3.3). This registry
 * is the closed set of legal glance targets per type — the schema validates against
 * it, so a config can never point the renderer at data that doesn't exist.
 *
 * These keys are the CONTRACT for adapter `normalize()` output (tile-dev) and the
 * glance view-model mapping (H3). Adding a metric = adding a key here + the adapter
 * producing it; nothing else references raw provider fields.
 */
export const DATA_SHAPE_KEYS = {
  'weather.current': [
    'tempF',
    'tempC',
    'condition',
    'vsYesterdayF',
    'vsYesterdayC',
    'humidityPct',
    'windMph',
  ],
  'sports.score': [
    'score',
    'clock',
    'status',
    'opponent',
    'result',
    'nextFixtureAt',
  ],
  'markets.quote': [
    'price',
    'change',
    'changePct',
    'direction',
    'currency',
    'prevClose',
  ],
  'news.headlines': ['headline', 'source', 'publishedAt', 'itemCount'],
  // rss.feed — the user-source tile (D-017 / ADR-005). Its normalized shape MIRRORS
  // news.headlines by design: an RSS feed IS a headline list, so it reuses the same
  // glance grammar and the same renderer chrome (text-only, React-escaped). Computed
  // CLIENT-SIDE from the user's own browser fetch of `feedUrl`; nothing transits our
  // backend (clientDirect). Each key documented (matches news semantics):
  //   headline    — title of the FIRST/top feed item (the one big fact / primary).
  //   source      — the feed's title/source name for the top item (delta context).
  //   publishedAt — ISO-8601 publish time of the top item (renderer formats it).
  //   itemCount   — number of items surfaced this fetch (≥0; drives "and N more").
  'rss.feed': ['headline', 'source', 'publishedAt', 'itemCount'],
  'clock.local': ['time', 'date', 'weekday', 'tz'],
  // calendar.peek — the normalized CalendarData the client-direct provider emits
  // (ADR-004). All glance targets are REAL metrics, computed client-side from the
  // user's Google events; none of it ever transits our backend.
  //   nextEventTitle        — title of the soonest upcoming event (the one big fact)
  //   nextEventStartISO      — that event's start, ISO-8601 (feeds relative-time render)
  //   minutesUntilNext       — whole minutes until the next event starts (numeric delta)
  //   eventsRemainingToday   — count of events left today (glance "3 more today")
  //   allDayCount            — count of all-day events in the window (state/badge)
  'calendar.peek': [
    'nextEventTitle',
    'nextEventStartISO',
    'minutesUntilNext',
    'eventsRemainingToday',
    'allDayCount',
  ],
  'markets.realtime': ['price', 'change', 'changePct', 'direction', 'currency', 'prevClose'],
  'brief.ai': ['summary', 'generatedAt', 'sourceCount'],
  'alert.generic': ['message', 'firedAt', 'severity'],
  // ---- Local utility widgets (D-015) — display data computed LOCALLY from
  // params (+ the current time for countdown), like clock derives from `Date`.
  // Every key is a scalar (string/number) a glanceView can render directly; no
  // booleans (a glance metric is a value, not a flag — booleans became scalars
  // below). None of this ever transits a server; it is derived in the client.

  // note.sticky — computed from { text }:
  //   preview   — first ~80 chars of the body, collapsed to a single line
  //               (newlines→spaces), '' when the note is empty. The primary fact.
  //   charCount — number of characters in the body (0 for an empty note).
  'note.sticky': ['preview', 'charCount'],

  // todo.list — computed from { items }:
  //   doneCount      — number of items with done === true.
  //   totalCount     — total number of items.
  //   remainingCount — items still to do (totalCount − doneCount); 0 ⇒ all done
  //                    (replaces a boolean `allDone`: a scalar the app maps to a
  //                    positive/neutral state tint at render, needing no flag key).
  //   nextItemText   — text of the first not-done item in ORDER, '' if none remain.
  'todo.list': ['doneCount', 'totalCount', 'remainingCount', 'nextItemText'],

  // countdown.event — computed from { label, targetISO } and the current time:
  //   targetLabel  — the event label (string), echoed for the delta/context slot.
  //   daysLeft     — whole days until the target; NEGATIVE once the target passes.
  //   hoursLeft    — total whole hours remaining until the target; negative allowed
  //                  (the finer-grained companion to daysLeft for near-term events).
  //   sinceOrUntil — 'until' before the target, 'since' after it (a scalar that
  //                  replaces a boolean `isPast`; drives "in 3 days" vs "3 days ago").
  'countdown.event': ['targetLabel', 'daysLeft', 'hoursLeft', 'sinceOrUntil'],
} as const satisfies Record<TileType, readonly string[]>;

/** Union of every legal metric key across all tile types. */
export type MetricKey = (typeof DATA_SHAPE_KEYS)[TileType][number];

/** Legal metric keys for a specific tile type. */
export type MetricKeyFor<T extends TileType> = (typeof DATA_SHAPE_KEYS)[T][number];

/** Membership check used by the glance validator (ADR-002 §3.3). Pure, total. */
export function isMetricOf(type: TileType, key: string): boolean {
  const keys = DATA_SHAPE_KEYS[type] as readonly string[];
  return keys.includes(key);
}
